
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Typography,
  Chip,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Checkbox,
  Paper,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  ListItemSecondaryAction,
  Divider,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  FileCopy as FileCopyIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  ClearAll as ClearAllIcon,
  PlayArrow as StartIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Label as LabelIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import CampaignCard from './components/CampaignCard';
import CampaignFormDialog from './components/CampaignFormDialog';
import ContactSelectionDialog from './components/ContactSelectionDialog';
import ContactStatusesDialog from './components/ContactStatusesDialog';
import CampaignStatsDialog from './components/CampaignStatsDialog';
import { format } from 'date-fns';
import * as campaignService from '../../services/campaignService';
import * as instanceService from '../../services/instanceService';
import * as contactService from '../../services/contactService';
import {getCampaignContactStatuses} from "../../services/campaignService";

const DEFAULT_FORM_DATA = {
  name: '',
  description: '',
  status: 'draft',
  start_date: '',
  end_date: '',
  working_hours_start: 9,
  working_hours_end: 18,
  sending_delay: 0,
  instances: [],
  targetGroups: [],
  message_content: '',
  attachment: null,
  target_tags: [], // НОВОЕ ПОЛЕ
  stop_tags: ['STOP'] // НОВОЕ ПОЛЕ с дефолтным значением
};

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [contactSelectionDialogOpen, setContactSelectionDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [contactStatusesDialogOpen, setContactStatusesDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [campaignStats, setCampaignStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Form modes and state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [campaignContacts, setCampaignContacts] = useState([]);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [contactStatuses, setContactStatuses] = useState([]);

  // Available data
  const [availableInstances, setAvailableInstances] = useState([]);
  const [availableContactTags, setAvailableContactTags] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Constants
  const statusColors = {
    active: 'success',
    scheduled: 'info',
    completed: 'default',
    draft: 'warning',
    paused: 'error'
  };

  const tabFilters = {
    0: 'all',
    1: 'active',
    2: 'scheduled',
    3: 'completed',
    4: 'draft'
  };

  // Data loading
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await campaignService.getAllCampaigns();

      if (Array.isArray(data)) {
        const formattedCampaigns = data.map((campaign) => {
          // Нормализация тегов
          const normalizeTagsArray = (tags) => {
            if (!tags) return [];
            if (Array.isArray(tags)) return tags;
            if (typeof tags === 'string') {
              try {
                const parsed = JSON.parse(tags);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return tags.split(',').map(tag => tag.trim()).filter(Boolean);
              }
            }
            return [];
          };

          return {
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        startDate: campaign.start_date ? new Date(campaign.start_date) : null,
        endDate: campaign.end_date ? new Date(campaign.end_date) : null,
            sending_delay: campaign.sending_delay || 0,
            working_hours_start: campaign.working_hours_start ?? 9,
            working_hours_end: campaign.working_hours_end ?? 18,
            description: campaign.description || '',
            message: campaign.message_content || '',

            // ИСПРАВЛЕННЫЕ ПОЛЯ: правильная нормализация тегов
            target_tags: normalizeTagsArray(campaign.target_tags),
            stop_tags: normalizeTagsArray(campaign.stop_tags),

            // ДОБАВЛЯЕМ ПОЛЕ INSTANCES!
            Instances: campaign.Instances || [],

            instances: [],
            contactGroups: [],

            instances_total: campaign.instances_total || 0,
            instances_active: campaign.instances_active || 0,
            instances_blocked: campaign.instances_blocked || 0,

            contacts_total: campaign.contacts_total || 0,
            sent_count: campaign.sent_count || 0,
            failed_count: campaign.failed_count || 0,
            pending_count: campaign.pending_count || 0,
            progress: campaign.progress || 0,

            detailsLoaded: false
          };
        });

        setCampaigns(formattedCampaigns);
        console.log(`Loaded ${formattedCampaigns.length} campaigns with aggregated data`);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCampaignFullData = useCallback(async (campaignId) => {
    try {
      console.log(`Loading full data for campaign ${campaignId}`);
      const fullData = await campaignService.getCampaignFull(campaignId);

      // Обновляем состояние конкретной кампании
      setCampaigns(prev => prev.map(campaign =>
          campaign.id === campaignId
              ? {
                ...campaign,
                instances: fullData.instances || [],
                contactGroups: fullData.contacts || [],
                target_tags: fullData.target_tags || campaign.target_tags || [],
                stop_tags: fullData.stop_tags || campaign.stop_tags || [],
                detailsLoaded: true
              }
              : campaign
      ));

      return {
        ...fullData,
        // Убеждаемся, что теги присутствуют в возвращаемых данных
        target_tags: fullData.target_tags,
        stop_tags: fullData.stop_tags
      };
    } catch (error) {
      console.error(`Error loading full data for campaign ${campaignId}:`, error);
      return { instances: [], contacts: [] };
    }
  }, []);

  const fetchInstancesAndTags = useCallback(async () => {
    try {
      const [instances, tags] = await Promise.all([
        instanceService.getUserInstances().catch(() => []),
        contactService.getContactTags().catch(() => [])
      ]);

      setAvailableInstances(Array.isArray(instances) ? instances : []);
      setAvailableContactTags(Array.isArray(tags) ? tags : []);
    } catch (err) {
      console.error('Error fetching instances and tags:', err);
      setAvailableInstances([]);
      setAvailableContactTags([]);
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchCampaigns();
    fetchInstancesAndTags();
  }, [fetchCampaigns, fetchInstancesAndTags]);

  // Filtering
  const filteredCampaigns = useMemo(() => {
    const filter = tabFilters[tabValue];
    if (filter === 'all') return campaigns;
    return campaigns.filter(campaign => campaign.status === filter);
  }, [campaigns, tabValue]);

  // Event handlers
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleCreateCampaign = useCallback(() => {
    const now = new Date();
    const formattedDate = now.toISOString().slice(0, 16);

    setFormData({
      ...DEFAULT_FORM_DATA,
      start_date: formattedDate
    });
    setCampaignContacts([]);
    setIsEditMode(false);
    setSelectedCampaign(null);
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setIsEditMode(false);
    setIsViewOnlyMode(false);
    setSelectedCampaign(null);
    setCampaignContacts([]);
  }, []);




  const handleEditCampaign = useCallback(async (campaign) => {
    if (!campaign) {
      console.error('Cannot edit campaign: No campaign selected');
      showSnackbar('Error: No campaign selected', 'error');
      return;
    }

    setSelectedCampaign(campaign);
    
    if (campaign.status === 'active') {
      console.log('Opening active campaign in view-only mode');
      setIsEditMode(true);
      setIsViewOnlyMode(true);
    } else {
      setIsEditMode(true);
      setIsViewOnlyMode(false);
    }

    // ОТЛАДКА: проверим исходные данные кампании
    console.log('🔍 Original campaign data from list:', campaign);
    console.log('🏷️ Campaign target_tags from list:', campaign.target_tags);
    console.log('🛑 Campaign stop_tags from list:', campaign.stop_tags);

    let startDateFormatted = '';
        let endDateFormatted = '';
    if (campaign.startDate) {
      try {
        const startDate = new Date(campaign.startDate);
        startDateFormatted = startDate.toISOString().slice(0, 16);
      } catch (error) {
        console.error('Error formatting start date:', error);
      }
    }
    if (campaign.endDate) {
      try {
        const endDate = new Date(campaign.endDate);
        endDateFormatted = endDate.toISOString().slice(0, 16);
      } catch (error) {
        console.error('Error formatting end date:', error);
      }
    }

    try {
      // ⚠️ ВАЖНО: загружаем полные данные кампании из API
      console.log('📡 Loading full campaign data from API...');
      const fullData = campaign.detailsLoaded
          ? {
            instances: campaign.instances,
            contacts: campaign.contactGroups,
            target_tags: campaign.target_tags,
            stop_tags: campaign.stop_tags
          }
          : await loadCampaignFullData(campaign.id);

      console.log('📋 Full data from API:', fullData);
      console.log('🏷️ API target_tags:', fullData.target_tags);
      console.log('🛑 API stop_tags:', fullData.stop_tags);

      // Если полные данные не содержат теги, получаем их напрямую из API
      let campaignTags = { target_tags: [], stop_tags: ['STOP'] };

      if (!fullData.target_tags && !fullData.stop_tags) {
        console.log('⚠️ Tags not found in full data, fetching campaign by ID...');
        try {
          const campaignDetails = await campaignService.getCampaignById(campaign.id);
          console.log('📄 Campaign details from API:', campaignDetails);
          campaignTags.target_tags = campaignDetails.target_tags || [];
          campaignTags.stop_tags = campaignDetails.stop_tags || ['STOP'];
        } catch (error) {
          console.error('Error fetching campaign details:', error);
        }
      } else {
        campaignTags.target_tags = fullData.target_tags || [];
        campaignTags.stop_tags = fullData.stop_tags || ['STOP'];
      }

      // Обработка тегов с нормализацией
      const normalizeTagsArray = (tags) => {
        console.log('🔄 Normalizing tags:', tags, 'Type:', typeof tags);
        if (!tags) return [];
        if (Array.isArray(tags)) return tags;
        if (typeof tags === 'string') {
          try {
            // Если это JSON строка
            const parsed = JSON.parse(tags);
            console.log('✅ Parsed JSON tags:', parsed);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            // Если это строка с разделителями
            const split = tags.split(',').map(tag => tag.trim()).filter(Boolean);
            console.log('✅ Split string tags:', split);
            return split;
          }
        }
        return [];
      };

      const targetTags = normalizeTagsArray(campaignTags.target_tags);
      const stopTags = normalizeTagsArray(campaignTags.stop_tags);

      console.log('✅ Final normalized target_tags:', targetTags);
      console.log('✅ Final normalized stop_tags:', stopTags);

      const newFormData = {
        name: campaign.name,
        description: campaign.description || '',
        status: campaign.status,
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        working_hours_start: campaign.working_hours_start ?? 9,
        working_hours_end: campaign.working_hours_end ?? 18,
        sending_delay: campaign.sending_delay ?? 0,
        instances: fullData.instances?.map(instance => instance.id) || [],
        targetGroups: fullData.contacts?.map(contact =>
            typeof contact === 'object' ? contact.id : contact
        ) || [],
        message_content: campaign.message || '',
        attachment: campaign.attachment || null,
        target_tags: targetTags,
        stop_tags: stopTags.length > 0 ? stopTags : ['STOP']
      };

      console.log('🎯 Setting form data:', newFormData);
      setFormData(newFormData);

      setCampaignContacts(fullData.contacts || []);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error loading campaign data:', error);
      showSnackbar('Error loading campaign data', 'error');
    }
  }, [loadCampaignFullData, showSnackbar]);

  // Contacts
  const handleOpenContactSelectionDialog = useCallback(() => {
    setContactSelectionDialogOpen(true);
  }, []);

  const handleCloseContactSelectionDialog = useCallback(() => {
    setContactSelectionDialogOpen(false);
  }, []);

  const handleSaveCampaignContacts = useCallback(async (updatedContacts) => {
    setCampaignContacts(updatedContacts);
    try {
      if (selectedCampaign) {
        await campaignService.updateCampaignContacts(
            selectedCampaign.id,
            updatedContacts.map((c) => c.id)
        );
        showSnackbar('Campaign contacts updated successfully');
      }
    } catch (err) {
      console.error('Failed to update campaign contacts:', err);
      showSnackbar('Failed to update campaign contacts', 'error');
    }
  }, [selectedCampaign, showSnackbar]);

  // Contact statuses dialog handlers
  const handleViewContactStatuses = useCallback(async (campaign) => {
    try {
      setLoading(true);
      setSelectedCampaign(campaign);

      const campaignContactsData = await campaignService.getCampaignContactStatuses(campaign.id);
      setContactStatuses(campaignContactsData || []);
      setContactStatusesDialogOpen(true);
    } catch (err) {
      console.error('Failed to load campaign contact statuses:', err);
      showSnackbar('Failed to load campaign contact statuses', 'error');
      setContactStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const handleCloseContactStatusesDialog = useCallback(() => {
    setContactStatusesDialogOpen(false);
    setSelectedCampaign(null);
    setContactStatuses([]);
  }, []);

  const handleInputChange = useCallback((e) => {
    console.log('handleInputChange called:', e.target.name, e.target.value);
    const { name, value, files } = e.target;

    setFormData(prev => {
      const newData = { ...prev };

      if (name === 'attachment' && files && files.length > 0) {
        newData[name] = files[0];
      } else {
        newData[name] = value;
      }

      console.log('Form data updated:', newData);
      return newData;
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setLoading(true);

      const processedData = { ...formData };

      if (processedData.start_date) {
        const startDate = new Date(processedData.start_date);
        processedData.start_date = startDate.toISOString();
      }
      if (processedData.end_date) {
        const endDate = new Date(processedData.end_date);
        processedData.end_date = endDate.toISOString();
      } else {
        // Explicitly send null to clear end_date in DB when toggle is off
        processedData.end_date = null;
      }

      if (!isEditMode) {
        const result = await campaignService.createCampaign(processedData);
        const createdCampaign = result.campaign;

        const promises = [];

        if (processedData.instances?.length > 0) {
          promises.push(
              campaignService.addInstancesToCampaign(createdCampaign.id, processedData.instances)
          );
        }

        if (campaignContacts.length > 0) {
          const contactIds = campaignContacts.map(contact => contact.id);
          promises.push(
              campaignService.addContactsToCampaign(createdCampaign.id, contactIds)
          );
        }

        await Promise.all(promises);
        showSnackbar('Campaign created successfully');
      } else {
        await campaignService.updateCampaign(selectedCampaign.id, processedData);

        const campaignInstances = await campaignService.getCampaignInstances(selectedCampaign.id);
        const oldIds = campaignInstances.map(instance => instance.id);
        const newIds = processedData.instances;

        const toAdd = newIds.filter(id => !oldIds.includes(id));
        const toRemove = oldIds.filter(id => !newIds.includes(id));

        const promises = [];
        if (toAdd.length > 0) {
          promises.push(campaignService.addInstancesToCampaign(selectedCampaign.id, toAdd));
        }
        if (toRemove.length > 0) {
          promises.push(campaignService.removeInstancesFromCampaign(selectedCampaign.id, toRemove));
        }

        await Promise.all(promises);
        showSnackbar('Campaign updated successfully');
      }

      handleCloseDialog();
      fetchCampaigns();
    } catch (err) {
      console.error('Error saving campaign:', err);
      showSnackbar(err.message || 'Failed to save campaign', 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, isEditMode, selectedCampaign, campaignContacts, showSnackbar, handleCloseDialog, fetchCampaigns]);

  // Campaign actions
  const createCampaignActionHandler = useCallback((action, successMessage) =>
      async (campaign) => {
        try {
          setLoading(true);
          await action(campaign.id);
          showSnackbar(successMessage);
          fetchCampaigns();
        } catch (err) {
          console.error(`Error performing campaign action:`, err);
          showSnackbar(err.message || 'Action failed', 'error');
        } finally {
          setLoading(false);
        }
      }, [showSnackbar, fetchCampaigns]);

  const handlePauseCampaign = useMemo(() =>
          createCampaignActionHandler(campaignService.pauseCampaign, 'Campaign paused successfully'),
      [createCampaignActionHandler]
  );

  const handleStopCampaign = useMemo(() =>
          createCampaignActionHandler(campaignService.stopCampaign, 'Campaign stopped successfully'),
      [createCampaignActionHandler]
  );

  const handleStartCampaign = useMemo(() =>
          createCampaignActionHandler(campaignService.startCampaign, 'Campaign started successfully'),
      [createCampaignActionHandler]
  );

  const handleCopyCampaign = useMemo(() =>
          createCampaignActionHandler(campaignService.copyCampaign, 'Campaign copied successfully'),
      [createCampaignActionHandler]
  );

  const handleDeleteCampaign = useMemo(() =>
          createCampaignActionHandler(campaignService.deleteCampaign, 'Campaign deleted successfully'),
      [createCampaignActionHandler]
  );

  const handleSendTest = useCallback(async (campaign) => {
    try {
      const phone = window.prompt('Введите номер телефона (в международном формате, можно с +):');
      if (!phone) return;
      setLoading(true);
      await campaignService.sendCampaignTest(campaign.id, phone);
      showSnackbar('Тестовое сообщение отправлено');
    } catch (err) {
      console.error('Error sending test message:', err);
      const message = err?.response?.data?.message || err.message || 'Не удалось отправить тестовое сообщение';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const handleShareCampaign = useCallback(async (campaign) => {
    try {
      setLoading(true);
      const { url } = await campaignService.getCampaignShareLink(campaign.id);
      try {
        await navigator.clipboard.writeText(url);
        showSnackbar('Ссылка скопирована в буфер обмена');
      } catch {
        showSnackbar('Ссылка сгенерирована, открываем в новой вкладке');
      }
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error('Error generating share link:', err);
      const message = err?.response?.data?.message || err.message || 'Не удалось сгенерировать ссылку';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const handleViewStats = useCallback(async (campaign) => {
    try {
      setStatsLoading(true);
      setSelectedCampaign(campaign);
      const stats = await campaignService.getCampaignStats(campaign.id);
      setCampaignStats(stats);
      setStatsDialogOpen(true);
    } catch (err) {
      console.error('Error loading campaign stats:', err);
      showSnackbar('Failed to load campaign stats', 'error');
      setCampaignStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [showSnackbar]);

  const handleViewHistory = useCallback(async (campaign) => {
    try {
      setLoading(true);
      const history = await campaignService.getCampaignHistory(campaign.id);
      setCampaignHistory(history);
      setSelectedCampaign(campaign);
      setHistoryDialogOpen(true);
    } catch (err) {
      console.error('Error loading campaign history:', err);
      showSnackbar('Failed to load campaign history', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Campaigns
            </Typography>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateCampaign}
                disabled={loading}
            >
              Create Campaign
            </Button>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="All" />
              <Tab label="Active" />
              <Tab label="Scheduled" />
              <Tab label="Completed" />
              <Tab label="Draft" />
            </Tabs>
          </Box>

          {/* Loading */}
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Error */}
          {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
          )}

          {/* Campaigns Grid */}
          <Grid container spacing={3}>
            {filteredCampaigns.map((campaign) => (
                <Grid item xs={12} sm={6} md={4} key={campaign.id}>
                  <CampaignCard
                      campaign={campaign}
                      statusColors={statusColors}
                      onEdit={handleEditCampaign}
                      onViewHistory={handleViewHistory}
                      onViewContactStatuses={handleViewContactStatuses}
                      onPause={handlePauseCampaign}
                      onStop={handleStopCampaign}
                      onStart={handleStartCampaign}
                      onCopy={handleCopyCampaign}
                      onDelete={handleDeleteCampaign}
                      onSendTest={handleSendTest}
                      onShare={handleShareCampaign}
                      onViewStats={handleViewStats}
                  />
                </Grid>
            ))}
          </Grid>

          {/* Empty state */}
          {!loading && filteredCampaigns.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No campaigns found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create your first campaign to get started
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateCampaign}
                >
                  Create Campaign
                </Button>
              </Box>
          )}
        </Box>

        {/* Dialogs */}
        <CampaignFormDialog
            open={openDialog}
            onClose={handleCloseDialog}
            handleSave={handleSave}
            formData={formData}
            handleInputChange={handleInputChange}
            availableInstances={availableInstances}
            availableContactTags={availableContactTags} // ПЕРЕДАЕМ ДОСТУПНЫЕ ТЕГИ
            campaignContacts={campaignContacts}
            onOpenContactSelection={handleOpenContactSelectionDialog}
            isEditMode={isEditMode}
            isViewOnlyMode={isViewOnlyMode}
            loading={loading}
        />

        <ContactSelectionDialog
            open={contactSelectionDialogOpen}
            onClose={handleCloseContactSelectionDialog}
            campaignContacts={campaignContacts}
            onSave={handleSaveCampaignContacts}
        />

        <ContactStatusesDialog
            open={contactStatusesDialogOpen}
            onClose={handleCloseContactStatusesDialog}
            contactStatuses={contactStatuses}
            selectedCampaign={selectedCampaign}
        />

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Campaign History - {selectedCampaign?.name}
          </DialogTitle>
          <DialogContent>
            {campaignHistory.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaignHistory.map((entry, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {entry.timestamp ? format(new Date(entry.timestamp), 'dd.MM.yyyy HH:mm') : '-'}
                          </TableCell>
                          <TableCell>{entry.action || '-'}</TableCell>
                          <TableCell>
                            <Chip
                                label={entry.status || '-'}
                                color={statusColors[entry.status] || 'default'}
                                size="small"
                            />
                          </TableCell>
                          <TableCell>{entry.details || '-'}</TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No history available
                  </Typography>
                </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <CampaignStatsDialog
            open={statsDialogOpen}
            onClose={() => setStatsDialogOpen(false)}
            selectedCampaign={selectedCampaign}
            stats={campaignStats}
            loading={statsLoading}
        />

        {/* Snackbar */}
        <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DashboardLayout>
  );
};

export default CampaignsPage;